(() => {
  const OPTION_ATTRIBUTE = '_ge_purchase_option';
  const PUBLIC_OPTION_ATTRIBUTE = 'Purchase option';
  const OFFER_ATTRIBUTE = 'Final Sale offer';
  const SAVINGS_ATTRIBUTE = 'Final Sale savings';
  const ADMIN_FILTER_ATTRIBUTE = 'GreyExim order filter';
  const POLICY_ATTRIBUTE = 'Final Sale status';
  const SOURCE = 'greyexim-final-sale';

  const cartUrl = () => `${window.routes?.cart_url || '/cart'}.js`;
  const updateUrl = () => window.routes?.cart_update_url || '/cart/update.js';

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    });
    let body;
    try {
      body = await response.json();
    } catch (_error) {
      body = {};
    }
    if (!response.ok || body?.status || body?.errors) {
      throw new Error(body?.description || body?.message || body?.errors || `Cart request failed (${response.status})`);
    }
    return body;
  };

  const getCart = () => requestJson(cartUrl());

  class GreyEximFinalSale extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.unsubscribe = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event))
        : null;
      window.GreyEximFinalSale = this;
      this.setVisualSelection(this.dataset.selectedOption || 'regular');
      if (this.dataset.optimisticShell === 'true') {
        this.normalizationPromise = Promise.resolve();
        return;
      }
      if (this.dataset.resetOnly === 'true') {
        this.normalizationPromise = new Promise((resolve) => queueMicrotask(() => {
          if (!this.isConnected || this.updating) return resolve();
          this.resetFeatureState().catch(() => undefined).finally(resolve);
        }));
      } else if (
        this.dataset.selectionPersisted !== 'true'
        || this.dataset.persistedOption !== this.dataset.selectedOption
      ) {
        this.normalizationPromise = new Promise((resolve) => queueMicrotask(() => {
          if (!this.isConnected || this.updating) return resolve();
          this.applySelection(this.dataset.selectedOption || 'regular', { announce: false })
            .catch(() => undefined)
            .finally(resolve);
        }));
      } else {
        this.normalizationPromise = Promise.resolve();
      }
    }

    async resetFeatureState() {
      this.setBusy(true);
      try {
        const currentCart = await getCart();
        const discountCodes = this.activeDiscountCodes(currentCart);
        discountCodes.delete(this.finalSaleCode);
        const cart = await requestJson(updateUrl(), {
          method: 'POST',
          body: JSON.stringify({
            discount: [...discountCodes].join(','),
            attributes: {
              [OPTION_ATTRIBUTE]: '',
              [PUBLIC_OPTION_ATTRIBUTE]: '',
              [OFFER_ATTRIBUTE]: '',
              [SAVINGS_ATTRIBUTE]: '',
              [ADMIN_FILTER_ATTRIBUTE]: '',
              [POLICY_ATTRIBUTE]: '',
            },
          }),
        });
        this.syncCartPresentation(cart);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
        }
      } finally {
        this.setBusy(false);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.connected = false;
      if (window.GreyEximFinalSale === this) window.GreyEximFinalSale = null;
    }

    get selection() {
      return this.querySelector('input[name="greyexim-purchase-option"]:checked')?.value || 'regular';
    }

    get finalSaleCode() {
      return String(this.dataset.discountCode || '').trim().toUpperCase();
    }

    normalizeDiscountKey(value) {
      return String(value || '').trim().toUpperCase();
    }

    applicationMatches(application) {
      const title = this.normalizeDiscountKey(application?.title || application?.code);
      const configuredTitle = this.normalizeDiscountKey(this.dataset.discountTitle);
      return Boolean(title && (title === this.finalSaleCode || title === configuredTitle));
    }

    activeDiscountCodes(cart) {
      const codes = new Set();
      (cart?.discount_codes || []).forEach((entry) => {
        const code = this.normalizeDiscountKey(entry?.code || entry);
        if (code) codes.add(code);
      });
      (cart?.cart_level_discount_applications || []).forEach((application) => {
        if (application?.type !== 'discount_code') return;
        const code = this.normalizeDiscountKey(application?.title);
        if (code) codes.add(code);
      });
      return codes;
    }

    get eligibleProductIds() {
      return new Set([...this.querySelectorAll('[data-product-id]')].map((node) => String(node.dataset.productId)));
    }

    get eligibleVariantIds() {
      return new Set([...this.querySelectorAll('[data-variant-id]')].map((node) => String(node.dataset.variantId)));
    }

    isEligibleLine(line) {
      const productId = String(line?.product_id || '');
      const variantId = String(line?.variant_id || line?.id || '');
      return this.eligibleProductIds.has(productId) || this.eligibleVariantIds.has(variantId);
    }

    onChange(event) {
      const input = event.target.closest('input[name="greyexim-purchase-option"]');
      if (!input || input.disabled || !this.contains(input)) return;
      this.setVisualSelection(input.value);
      this.syncOptimisticSelectionPresentation(input.value);
      this.showStatus('');
      this.queueSelection(input.value);
    }

    queueSelection(option) {
      const safeOption = option === 'final_sale' ? 'final_sale' : 'regular';
      const revision = (this.selectionRevision || 0) + 1;
      this.selectionRevision = revision;
      this.queuedSelection = { option: safeOption, revision };
      if (this.selectionCommitPromise) return this.selectionCommitPromise;

      const commit = async () => {
        await this.normalizationPromise?.catch(() => undefined);
        while (this.queuedSelection) {
          const task = this.queuedSelection;
          this.queuedSelection = null;
          try {
            const cart = await this.applySelection(task.option, {
              announce: false,
              shouldPresent: () => task.revision === this.selectionRevision,
            });
            const committedOption = cart?.attributes?.[OPTION_ATTRIBUTE] === 'final_sale' ? 'final_sale' : 'regular';
            if (this.queuedSelection?.option === committedOption) this.queuedSelection = null;
          } catch (error) {
            if (task.revision !== this.selectionRevision) continue;
            const cart = await getCart().catch(() => null);
            if (cart) this.syncCartPresentation(cart);
            this.showStatus(error?.message || this.dataset.errorMessage, true);
          }
        }
      };

      this.selectionCommitPromise = commit().finally(() => {
        this.selectionCommitPromise = null;
      });
      return this.selectionCommitPromise;
    }

    async onCartUpdate(event) {
      if (!this.isConnected || event?.source === SOURCE || this.updating) return;
      const eventCart = event?.cartData;
      const hasCompleteCart = Array.isArray(eventCart?.items)
        && Object.prototype.hasOwnProperty.call(eventCart, 'item_count')
        && Object.prototype.hasOwnProperty.call(eventCart, 'total_price')
        && eventCart.attributes && typeof eventCart.attributes === 'object';
      let cart = hasCompleteCart ? eventCart : await getCart().catch(() => null);
      if (!cart) return;

      const persistedSelection = cart.attributes?.[OPTION_ATTRIBUTE] === 'final_sale' ? 'final_sale' : 'regular';
      const minimumCartValue = Math.max(0, Number(this.dataset.minimumCartValue || 0));
      const minimumMet = Number(cart.items_subtotal_price || 0) >= minimumCartValue;
      if (persistedSelection === 'final_sale' && !minimumMet) {
        this.setVisualSelection('regular');
        await this.applySelection('regular', { announce: false }).catch((error) => {
          this.showStatus(error?.message || this.dataset.errorMessage, true);
        });
        return;
      }

      this.setVisualSelection(persistedSelection);
      this.syncCartPresentation(cart);
    }

    setVisualSelection(option) {
      const safeOption = option === 'final_sale' ? 'final_sale' : 'regular';
      this.dataset.selectedOption = safeOption;
      this.querySelectorAll('.ge-final-sale__option').forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        const selected = input?.value === safeOption;
        label.classList.toggle('is-selected', selected);
        if (input && !input.disabled) input.checked = selected;
      });
    }

    setBusy(busy) {
      this.updating = busy;
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    showStatus(message = '', isError = false) {
      const status = this.querySelector('.ge-final-sale__status');
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', isError);
    }

    optionLabel(option) {
      return option === 'final_sale' ? this.dataset.finalSaleLabel : this.dataset.regularLabel;
    }

    offerAuditText() {
      const type = this.dataset.discountType === 'fixed_cart_amount'
        ? 'fixed amount per cart'
        : (this.dataset.discountType === 'fixed_amount' ? 'fixed amount per unit' : 'percentage');
      const terms = [
        this.dataset.discountTitle,
        `code ${this.finalSaleCode}`,
        this.dataset.offerVersion,
        `${type} ${this.dataset.discountValue}`,
        `minimum ${this.formatMoney(this.dataset.minimumCartValue)}`,
        `cap ${Number(this.dataset.maximumDiscount || 0) > 0 ? this.formatMoney(this.dataset.maximumDiscount) : 'none'}`,
        `scope ${this.dataset.eligibleScope || 'all'}`,
      ];
      if (this.dataset.startAt) terms.push(`starts ${this.dataset.startAt}`);
      if (this.dataset.endAt) terms.push(`ends ${this.dataset.endAt}`);
      return terms.join(' · ').slice(0, 240);
    }

    selectionAttributes(option, savingsCents = null) {
      const attributes = {
        [OPTION_ATTRIBUTE]: option,
        [PUBLIC_OPTION_ATTRIBUTE]: this.optionLabel(option),
        [OFFER_ATTRIBUTE]: option === 'final_sale' ? this.offerAuditText() : '',
        [ADMIN_FILTER_ATTRIBUTE]: option === 'final_sale' ? 'FINAL-SALE' : '',
        [POLICY_ATTRIBUTE]: option === 'final_sale'
          ? (this.dataset.finalSaleDescription || 'Non-returnable product*')
          : '',
      };
      if (savingsCents !== null) {
        attributes[SAVINGS_ATTRIBUTE] = option === 'final_sale' ? this.formatMoney(savingsCents) : '';
      } else if (option !== 'final_sale') {
        attributes[SAVINGS_ATTRIBUTE] = '';
      }
      return attributes;
    }

    async updateSelection(option, savingsCents = null, currentCart = null) {
      const cart = currentCart || await getCart();
      const discountCodes = this.activeDiscountCodes(cart);
      if (option === 'final_sale' && this.finalSaleCode) discountCodes.add(this.finalSaleCode);
      else discountCodes.delete(this.finalSaleCode);
      return requestJson(updateUrl(), {
        method: 'POST',
        body: JSON.stringify({
          discount: [...discountCodes].join(','),
          attributes: this.selectionAttributes(option, savingsCents),
        }),
      });
    }

    actualSavings(cart) {
      return (cart.items || []).reduce((total, line) => {
        if (!this.isEligibleLine(line)) return total;
        const directSavings = (line.discounts || []).reduce((sum, discount) => {
          return this.applicationMatches(discount) ? sum + Number(discount.amount || 0) : sum;
        }, 0);
        if (directSavings > 0) return total + directSavings;
        const lineSavings = (line.line_level_discount_allocations || []).reduce((sum, allocation) => {
          return this.applicationMatches(allocation?.discount_application)
            ? sum + Number(allocation.amount || 0)
            : sum;
        }, 0);
        return total + lineSavings;
      }, 0);
    }

    estimatedSavings(cart) {
      const eligible = (cart.items || []).filter((line) => this.isEligibleLine(line));
      const subtotal = eligible.reduce((sum, line) => sum + Number(line.final_line_price ?? line.original_line_price ?? 0), 0);
      const quantity = eligible.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      const value = Math.max(0, Number(this.dataset.discountValue || 0));
      let savings;
      if (this.dataset.discountType === 'fixed_cart_amount') {
        savings = Math.round(value * 100);
      } else if (this.dataset.discountType === 'fixed_amount') {
        savings = Math.round(value * 100) * quantity;
      } else {
        savings = Math.round((subtotal * Math.min(value, 100)) / 100);
      }
      const cap = Math.max(0, Number(this.dataset.maximumDiscount || 0));
      if (cap > 0) savings = Math.min(savings, cap);
      return Math.max(0, Math.min(savings, subtotal));
    }

    estimatedSavingsForValues(eligibleSubtotal, eligibleQuantity) {
      const subtotal = Math.max(0, Number(eligibleSubtotal || 0));
      const quantity = Math.max(0, Number(eligibleQuantity || 0));
      const value = Math.max(0, Number(this.dataset.discountValue || 0));
      let savings;
      if (this.dataset.discountType === 'fixed_cart_amount') {
        savings = Math.round(value * 100);
      } else if (this.dataset.discountType === 'fixed_amount') {
        savings = Math.round(value * 100) * quantity;
      } else {
        savings = Math.round((subtotal * Math.min(value, 100)) / 100);
      }
      const cap = Math.max(0, Number(this.dataset.maximumDiscount || 0));
      if (cap > 0) savings = Math.min(savings, cap);
      return Math.max(0, Math.min(savings, subtotal));
    }

    applyOptimisticAdd({ productId, variantId, quantity, unitPrice }) {
      const snapshot = {
        hidden: this.hidden,
        optimisticShell: this.dataset.optimisticShell,
        optimisticPending: this.dataset.optimisticPending,
        regularTotal: this.dataset.regularTotal,
        finalTotal: this.dataset.finalTotal,
        eligibleSubtotal: this.dataset.eligibleSubtotal,
        eligibleQuantity: this.dataset.eligibleQuantity,
        createdMarker: null,
      };
      const addedQuantity = Math.max(1, Number(quantity || 1));
      const addedSubtotal = Math.max(0, Number(unitPrice || 0)) * addedQuantity;
      const regularTotal = Math.max(0, Number(this.dataset.regularTotal || 0)) + addedSubtotal;
      const eligibleSubtotal = Math.max(0, Number(this.dataset.eligibleSubtotal || 0)) + addedSubtotal;
      const eligibleQuantity = Math.max(0, Number(this.dataset.eligibleQuantity || 0)) + addedQuantity;
      const savings = this.estimatedSavingsForValues(eligibleSubtotal, eligibleQuantity);
      const finalTotal = Math.max(0, regularTotal - savings);

      const existingMarker = [...this.querySelectorAll('.ge-final-sale__eligible-line')]
        .find((marker) => String(marker.dataset.variantId || '') === String(variantId || ''));
      if (!existingMarker) {
        const marker = document.createElement('span');
        marker.className = 'ge-final-sale__eligible-line';
        marker.hidden = true;
        marker.dataset.productId = String(productId || '');
        marker.dataset.variantId = String(variantId || '');
        marker.dataset.lineKey = '';
        this.prepend(marker);
        snapshot.createdMarker = marker;
      }

      this.dataset.optimisticShell = 'false';
      this.dataset.optimisticPending = 'true';
      this.dataset.regularTotal = String(regularTotal);
      this.dataset.finalTotal = String(finalTotal);
      this.dataset.eligibleSubtotal = String(eligibleSubtotal);
      this.dataset.eligibleQuantity = String(eligibleQuantity);
      this.hidden = false;
      this.syncOptimisticSelectionPresentation(this.dataset.selectedOption || 'regular');
      return snapshot;
    }

    rollbackOptimisticAdd(snapshot) {
      if (!snapshot) return;
      snapshot.createdMarker?.remove();
      this.dataset.optimisticShell = snapshot.optimisticShell || 'false';
      if (snapshot.optimisticPending) this.dataset.optimisticPending = snapshot.optimisticPending;
      else delete this.dataset.optimisticPending;
      this.dataset.regularTotal = snapshot.regularTotal || '0';
      this.dataset.finalTotal = snapshot.finalTotal || '0';
      this.dataset.eligibleSubtotal = snapshot.eligibleSubtotal || '0';
      this.dataset.eligibleQuantity = snapshot.eligibleQuantity || '0';
      this.hidden = snapshot.hidden;
      this.syncOptimisticSelectionPresentation(this.dataset.selectedOption || 'regular');
    }

    syncOptimisticSelectionPresentation(option) {
      const selected = option === 'final_sale' ? 'final_sale' : 'regular';
      const regularTotal = Math.max(0, Number(this.dataset.regularTotal || 0));
      const finalTotal = Math.max(0, Number(this.dataset.finalTotal || regularTotal));
      const savings = Math.max(0, regularTotal - finalTotal);
      const selectedTotal = selected === 'final_sale' ? finalTotal : regularTotal;

      this.updateMoney('[data-ge-regular-price]', regularTotal);
      this.updateMoney('[data-ge-original-price]', regularTotal);
      this.updateMoney('[data-ge-final-price]', finalTotal);
      const savingsNode = this.querySelector('[data-ge-savings]');
      if (savingsNode) {
        const label = this.dataset.savingsLabel || 'You save';
        savingsNode.textContent = `${label} ${this.formatMoney(savings)}`;
      }

      const drawer = this.closest('cart-drawer');
      if (!drawer) return;
      drawer.dataset.cartTotalPrice = String(selectedTotal);
      drawer.querySelectorAll('.totals__total-value').forEach((node) => {
        node.textContent = this.formatMoney(selectedTotal);
      });
      const renderedRows = [...drawer.querySelectorAll('[data-ge-final-sale-rendered-discount]')];
      renderedRows.forEach((node) => {
        node.hidden = selected !== 'final_sale' || savings <= 0;
      });
      drawer.querySelectorAll('[data-ge-final-sale-discount]').forEach((node) => {
        node.hidden = selected !== 'final_sale' || savings <= 0 || renderedRows.length > 0;
        const amount = node.querySelector('[data-ge-final-sale-discount-amount]');
        if (amount) amount.textContent = `-${this.formatMoney(savings)}`;
        const list = node.closest('.discounts');
        if (list) list.hidden = ![...list.children].some((item) => !item.hidden);
      });
    }

    async applySelection(option, { announce = true, shouldPresent = () => true } = {}) {
      const safeOption = option === 'final_sale' ? 'final_sale' : 'regular';
      this.setBusy(true);
      if (announce) this.showStatus(this.dataset.updatingMessage || 'Updating purchase option…');
      try {
        let cart = await getCart();
        const estimatedSavings = safeOption === 'final_sale' ? this.estimatedSavings(cart) : 0;
        cart = await this.updateSelection(safeOption, estimatedSavings, cart);
        let savings = this.actualSavings(cart);

        if (safeOption === 'final_sale' && Number(this.dataset.discountValue || 0) > 0 && savings <= 0) {
          cart = await getCart();
          savings = this.actualSavings(cart);
        }

        if (safeOption === 'final_sale' && Number(this.dataset.discountValue || 0) > 0 && savings <= 0) {
          await this.updateSelection('regular', 0, cart);
          cart = await getCart();
          if (shouldPresent()) {
            this.setVisualSelection('regular');
            this.syncCartPresentation(cart);
            this.showStatus(this.dataset.errorMessage || 'Final Sale is unavailable.', true);
          }
          return cart;
        }

        if (savings !== estimatedSavings) {
          cart = await this.updateSelection(safeOption, savings, cart);
        }
        if (shouldPresent()) {
          this.setVisualSelection(safeOption);
          this.syncCartPresentation(cart);
          if (announce) {
            this.showStatus(
              safeOption === 'final_sale'
                ? (this.dataset.appliedMessage || 'Final Sale savings applied.')
                : (this.dataset.regularAppliedMessage || 'Regular Purchase selected.'),
            );
          } else {
            this.showStatus('');
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
        }
        return cart;
      } finally {
        this.setBusy(false);
      }
    }

    syncCartPresentation(cart) {
      const selected = cart.attributes?.[OPTION_ATTRIBUTE] === 'final_sale' ? 'final_sale' : 'regular';
      const applied = this.actualSavings(cart);
      const estimated = this.estimatedSavings(cart);
      const savings = applied || estimated;
      const currentTotal = Number(cart.total_price || 0);
      const regularTotal = selected === 'final_sale' ? currentTotal + applied : currentTotal;
      const finalTotal = selected === 'final_sale' ? currentTotal : Math.max(0, currentTotal - estimated);

      this.setVisualSelection(selected);
      this.dataset.regularTotal = String(regularTotal);
      this.dataset.finalTotal = String(finalTotal);
      this.dataset.eligibleSubtotal = String((cart.items || [])
        .filter((line) => this.isEligibleLine(line))
        .reduce((sum, line) => sum + Number(line.original_line_price || line.final_line_price || 0), 0));
      this.dataset.eligibleQuantity = String((cart.items || [])
        .filter((line) => this.isEligibleLine(line))
        .reduce((sum, line) => sum + Number(line.quantity || 0), 0));
      this.updateMoney('[data-ge-regular-price]', regularTotal);
      this.updateMoney('[data-ge-original-price]', regularTotal);
      this.updateMoney('[data-ge-final-price]', finalTotal);
      const savingsNode = this.querySelector('[data-ge-savings]');
      if (savingsNode) {
        const label = this.dataset.savingsLabel || 'You save';
        savingsNode.textContent = `${label} ${this.formatMoney(savings)}`;
      }

      const drawer = this.closest('cart-drawer');
      if (drawer) {
        let needsCanonicalRefresh = false;
        drawer.dataset.cartItemCount = String(cart.item_count || 0);
        drawer.dataset.cartTotalPrice = String(currentTotal);
        drawer.querySelectorAll('.totals__total-value').forEach((node) => {
          node.textContent = this.formatMoney(currentTotal);
        });
        const renderedFinalSaleRows = [...drawer.querySelectorAll('[data-ge-final-sale-rendered-discount]')];
        renderedFinalSaleRows.forEach((node) => {
          node.hidden = selected !== 'final_sale' || applied <= 0;
        });
        drawer.querySelectorAll('[data-ge-final-sale-discount]').forEach((node) => {
          node.hidden = selected !== 'final_sale' || applied <= 0 || renderedFinalSaleRows.length > 0;
          const title = node.querySelector('[data-ge-final-sale-discount-title]');
          const amount = node.querySelector('[data-ge-final-sale-discount-amount]');
          if (title) title.textContent = this.dataset.discountTitle || 'Final Sale';
          if (amount) amount.textContent = `-${this.formatMoney(applied)}`;
          const list = node.closest('.discounts');
          if (list) list.hidden = ![...list.children].some((item) => !item.hidden);
        });

        const rows = [...drawer.querySelectorAll('#CartDrawer-CartItems .cart-item[data-cart-line-key]')];
        const assignedLineKeys = new Set();
        rows.forEach((row, index) => {
          const previousKey = row.dataset.cartLineKey;
          let line = typeof drawer.findCartLine === 'function'
            ? drawer.findCartLine(cart, row, previousKey, row.dataset.cartQuantity)
            : (cart.items || []).find((item) => String(item.key) === String(previousKey));

          if (line && assignedLineKeys.has(String(line.key))) {
            const variantId = row.dataset.cartVariantId;
            line = (cart.items || []).find((item) => (
              String(item.variant_id || item.id) === String(variantId || '')
              && !assignedLineKeys.has(String(item.key))
            )) || null;
          }
          if (!line && cart.items?.[index] && !assignedLineKeys.has(String(cart.items[index].key))) {
            line = cart.items[index];
          }
          if (!line) {
            needsCanonicalRefresh = true;
            return;
          }

          assignedLineKeys.add(String(line.key));
          if (typeof drawer.syncRowFromCartLine === 'function') drawer.syncRowFromCartLine(row, line);
        });

        const eligibleLines = (cart.items || []).filter((line) => this.isEligibleLine(line));
        const markers = [...this.querySelectorAll('.ge-final-sale__eligible-line')];
        const assignedMarkerKeys = new Set();
        markers.forEach((marker) => {
          const previousKey = marker.dataset.lineKey;
          const variantId = marker.dataset.variantId;
          const productId = marker.dataset.productId;
          const line = eligibleLines.find((item) => (
            String(item.key) === String(previousKey)
            && !assignedMarkerKeys.has(String(item.key))
          )) || eligibleLines.find((item) => (
            String(item.variant_id || item.id) === String(variantId || '')
            && !assignedMarkerKeys.has(String(item.key))
          )) || eligibleLines.find((item) => (
            String(item.product_id || '') === String(productId || '')
            && !assignedMarkerKeys.has(String(item.key))
          ));
          if (!line) {
            needsCanonicalRefresh = true;
            return;
          }
          assignedMarkerKeys.add(String(line.key));
          marker.dataset.lineKey = String(line.key);
        });

        if (rows.length !== (cart.items || []).length || markers.length !== eligibleLines.length) {
          needsCanonicalRefresh = true;
        }
        if (needsCanonicalRefresh) drawer.requestCanonicalRefresh?.();
      }
    }

    updateMoney(selector, cents) {
      const node = this.querySelector(selector);
      if (node) node.textContent = this.formatMoney(cents);
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      const locale = document.documentElement.lang || 'en-IN';
      const currency = window.Shopify?.currency?.active || 'INR';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);
      } catch (_error) {
        return `₹${value.toFixed(2)}`;
      }
    }

    async verifyBeforeCheckout(button) {
      await this.normalizationPromise;
      await this.selectionCommitPromise;
      if (this.selection !== 'final_sale') return true;
      button.setAttribute('aria-busy', 'true');
      this.showStatus(this.dataset.verifyingMessage || 'Verifying Final Sale savings…');
      try {
        const cart = await getCart();
        const selectionValid = cart.attributes?.[OPTION_ATTRIBUTE] === 'final_sale';
        const savingsValid = Number(this.dataset.discountValue || 0) <= 0 || this.actualSavings(cart) > 0;
        if (!selectionValid || !savingsValid) {
          this.showStatus(this.dataset.errorMessage || 'Final Sale savings could not be verified.', true);
          return false;
        }
        return true;
      } catch (error) {
        this.showStatus(error?.message || this.dataset.errorMessage, true);
        return false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    reconcileSection(html) {
      if (!html || this.updating) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const source = documentFragment.querySelector('greyexim-final-sale');
      if (!source) {
        this.remove();
        return;
      }
      if (this.dataset.optimisticPending === 'true') {
        const currentMarkers = [...this.querySelectorAll('.ge-final-sale__eligible-line')];
        const sourceMarkers = [...source.querySelectorAll('.ge-final-sale__eligible-line')];
        if (currentMarkers.length === sourceMarkers.length) {
          currentMarkers.forEach((marker, index) => {
            const sourceMarker = sourceMarkers[index];
            marker.dataset.productId = sourceMarker.dataset.productId || '';
            marker.dataset.variantId = sourceMarker.dataset.variantId || '';
            marker.dataset.lineKey = sourceMarker.dataset.lineKey || '';
          });
          ['regularTotal', 'finalTotal', 'eligibleSubtotal', 'eligibleQuantity'].forEach((key) => {
            if (source.dataset[key] !== undefined) this.dataset[key] = source.dataset[key];
          });
          this.dataset.selectionPersisted = source.dataset.selectionPersisted || 'false';
          this.dataset.persistedOption = source.dataset.persistedOption || '';
          this.setVisualSelection(source.dataset.selectedOption || 'regular');
          delete this.dataset.optimisticPending;
          this.syncOptimisticSelectionPresentation(this.dataset.selectedOption || 'regular');
          return;
        }
      }
      const currentSignature = [...this.querySelectorAll('[data-line-key]')].map((node) => node.dataset.lineKey).join('|');
      const sourceSignature = [...source.querySelectorAll('[data-line-key]')].map((node) => node.dataset.lineKey).join('|');
      if (currentSignature !== sourceSignature || source.dataset.selectedOption !== this.dataset.selectedOption) {
        this.replaceWith(source);
      }
    }
  }

  if (!customElements.get('greyexim-final-sale')) {
    customElements.define('greyexim-final-sale', GreyEximFinalSale);
  }

  window.GreyEximFinalSaleCheckoutGate = async (button) => {
    const manager = button?.closest('cart-drawer')?.querySelector('greyexim-final-sale')
      || document.querySelector('cart-drawer greyexim-final-sale');
    if (!manager) return true;
    await manager.normalizationPromise;
    return manager.selection !== 'final_sale' || manager.verifyBeforeCheckout(button);
  };
})();
